import { useContext, useEffect, useState } from 'react'
import { useAppDispatch, useAppSelector } from './../state/app-store'
import Link from 'next/link'
import {
	Alert,
	Box,
	Button,
	Card,
	Divider,
	Grid,
	Group,
	List,
	Menu,
	Modal,
	Tabs,
	Text,
	ThemeIcon,
} from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import {
	IconLogout,
	IconMountain,
	IconPencil,
	IconPhotoUp,
	IconTrekking,
} from '@tabler/icons-react'
import { useRouter } from 'next/router'
import NextImage from 'next/image'

import * as Types from 'declarations'
import {
	isGuest as isGuestSelector,
	isLoginOpen as isLoginOpenSelector,
	isSignUpOpen as isSignUpOpenSelector,
	isUser as isUserSelector,
	isGuide as isGuideSelector,
	isUserAdmin as isUserAdminSelector,
} from '../state/selectors'
import { VersionContext } from '../contexts/VersionContext'
import { closeSignUp, logout } from './../state/actions'
import LoginForm from './LoginForm'
import RegisterForm from './RegisterForm'
import ResetPasswordForm from './ResetPasswordForm'
import { useIsMobile } from 'lib/useIsMobile'

import { getDefaultImage } from 'lib/helper'
import { ACCENT_COLOUR } from 'pages/_app'

const JoinCommunity = () => {
	const version = useContext(VersionContext)?.version

	return (
		<Text fw={500}>
			Join a community of{' '}
			{version === Types.VERSION.PEAKS ? 'mountaineers' : 'trekkers'}
		</Text>
	)
}

const AccountModal = () => {
	const [opened, { open, close }] = useDisclosure(false)
	const version = useContext(VersionContext).version
	const router = useRouter()
	const isMobile = useIsMobile()

	const isGuest = useAppSelector(isGuestSelector)
	const isUser = useAppSelector(isUserSelector)
	const isGuide = useAppSelector(isGuideSelector)
	const isAdmin = useAppSelector(isUserAdminSelector)

	const isSignUpOpen = useAppSelector(isSignUpOpenSelector)
	const isLogInOpen = useAppSelector(isLoginOpenSelector)

	useEffect(() => {
		if (isSignUpOpen) {
			open()
			setActiveTab('join')
		}
	}, [isSignUpOpen])

	useEffect(() => {
		if (isLogInOpen) {
			open()
			setActiveTab('login')
		}
	}, [isLogInOpen])

	const [activeTab, setActiveTab] = useState<string | null>('login')

	const dispatch = useAppDispatch()

	const logOut = () => {
		dispatch(logout())
		// Only navigate when actually elsewhere — pushing the current URL
		// throws "Invariant: attempted to hard navigate to the same URL"
		if (router.pathname !== '/') {
			router.push('/')
		}
	}

	const closeModal = () => {
		setActiveTab('login')
		close()
		dispatch(closeSignUp())
	}
	const isForcedGuideLogin = router.asPath.includes('#open-login-guide') // Check if the URL contains the fragment

	const isEmailAlreadyRegistered = router.asPath.includes(
		'#email-already-registered',
	)

	const iconProps = {
		size: 24,
		radius: 'xl',
		color: ACCENT_COLOUR,
	}

	return (
		<>
			<Modal
				opened={opened}
				onClose={closeModal}
				title={`Guided ${
					version === Types.VERSION.PEAKS ? 'Peaks' : 'Trekking'
				} - ${
					version === Types.VERSION.PEAKS ? 'Mountaineering' : 'IconTrekking'
				} community`}
				size="xl"
				// show above mobile menu
				zIndex={15000}
			>
				{isGuest && (
					<Box>
						{isForcedGuideLogin && (
							<Alert color={'orange'} title="Please log in" mb="xl">
								Log in first to access your dashboard.
							</Alert>
						)}
						{isEmailAlreadyRegistered && (
							<Alert color={'orange'} title="Please log in" mb="xl">
								That email is already registered. Please log in.
							</Alert>
						)}

						<Grid>
							{!isMobile && (
								<Grid.Col span={7}>
									<Box pr="xl">
										<Card shadow="sm" padding="lg" radius="md" withBorder>
											<Card.Section>
												<NextImage
													src={getDefaultImage(version)}
													alt={`Guided ${
														version === Types.VERSION.PEAKS
															? 'peaks'
															: 'trekking'
													}`}
													height={200}
													width={404}
												/>
											</Card.Section>

											<Group justify="apart" mt="md" mb="xs">
												<JoinCommunity />
											</Group>

											<Text size="sm" color="dimmed" component="div">
												<List spacing="xs" size="sm" center>
													<List.Item
														icon={
															<ThemeIcon {...iconProps}>
																<IconTrekking size="1rem" />
															</ThemeIcon>
														}
													>
														Find affordable local guides for peaks
													</List.Item>
													<List.Item
														icon={
															<ThemeIcon {...iconProps}>
																<IconPhotoUp size="1rem" />
															</ThemeIcon>
														}
													>
														Contribute pictures of routes
													</List.Item>
													{/* // todo: enable these once features are ready */}
													{/* <List.Item>
									Suggest new routes
								</List.Item>
								<List.Item>
									Wishlist trips you&apos;re interested in
								</List.Item> */}
													<List.Item
														icon={
															<ThemeIcon {...iconProps}>
																<IconPencil size="1rem" />
															</ThemeIcon>
														}
													>
														Edit/correct/add information for routes
													</List.Item>
													{/* // todo: ask/answer questions
								// todo: get notified about new routes 
								// todo: receive discounted trips */}
												</List>
											</Text>

											{/* <Button
										variant="light"
										color="blue"
										fullWidth
										mt="md"
										radius="md"
										// onClick={openSignUp}
									>
										[Join]
									</Button> */}
										</Card>
									</Box>
								</Grid.Col>
							)}

							<Grid.Col span={isMobile ? 12 : 5}>
								<Box>
									{isMobile && (
										<>
											<JoinCommunity />
											<br />
										</>
									)}
									<Tabs
										variant="outline"
										value={activeTab}
										onChange={setActiveTab}
									>
										<Tabs.List>
											<Tabs.Tab value="login">Login</Tabs.Tab>
											<Tabs.Tab value="join">Join</Tabs.Tab>
										</Tabs.List>
										<Tabs.Panel value="login" pt="xs">
											<LoginForm onLogin={closeModal} />
											<Divider my="sm" />
											<Button
												variant={'outline'}
												onClick={() => setActiveTab('forgot_password')}
												size="xs"
											>
												Forgot password?
											</Button>
										</Tabs.Panel>

										<Tabs.Panel value="join" pt="xs">
											<RegisterForm onRegister={closeModal} />
										</Tabs.Panel>

										<Tabs.Panel value="forgot_password" pt="xs">
											<ResetPasswordForm />
										</Tabs.Panel>
									</Tabs>
								</Box>
							</Grid.Col>
						</Grid>
					</Box>
				)}
			</Modal>

			{isGuest && (
				<>
					<Button onClick={open} variant="outline" fullWidth>
						Access Community
					</Button>
				</>
			)}
			{isGuide && version === Types.VERSION.PEAKS && (
				<>
					<Link href="/guide">
						<Button mr="sm" fullWidth>
							Guide section
						</Button>
					</Link>
				</>
			)}
			{(isUser || isGuide) && (
				<Menu shadow="md" width={200}>
					<Menu.Target>
						<Button variant="outline">Account&nbsp;&#x25BC;</Button>
					</Menu.Target>

					<Menu.Dropdown>
						{isGuide && version === Types.VERSION.PEAKS && (
							<>
								<Link href="/guide">
									<Menu.Item leftSection={<IconMountain size={14} />}>
										Guide section
									</Menu.Item>
									<Menu.Divider />
								</Link>
							</>
						)}

						<Menu.Item
							onClick={() => logOut()}
							leftSection={<IconLogout size={14} />}
						>
							Log out
						</Menu.Item>
					</Menu.Dropdown>
				</Menu>
			)}
			{isAdmin && (
				<>
					<Button
						onClick={() => logOut()}
						// fullWidth
						variant="outline"
					>
						Logout
					</Button>
				</>
			)}
		</>
	)
}

export default AccountModal
